import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getPublicAssetPath } from '../config';

const LandingPage: React.FC = () => {
  const { darkMode, toggleDarkMode } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-300 relative overflow-hidden ${
      darkMode ? 'bg-black text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Gradient Orbs */}
        <div className={`absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 ${
          darkMode ? 'bg-violet-600' : 'bg-violet-200'
        }`} style={{ transform: 'translate(-50%, -50%)' }}></div>
        <div className={`absolute top-1/3 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 ${
          darkMode ? 'bg-purple-600' : 'bg-purple-200'
        }`} style={{ transform: 'translate(50%, -50%)' }}></div>
        <div className={`absolute bottom-0 left-1/2 w-96 h-96 rounded-full blur-3xl opacity-20 ${
          darkMode ? 'bg-blue-600' : 'bg-blue-200'
        }`} style={{ transform: 'translate(-50%, 50%)' }}></div>
        
        {/* Grid Pattern */}
        <div 
          className={`absolute inset-0 ${
            darkMode 
              ? 'opacity-[0.03]' 
              : 'opacity-[0.02]'
          }`}
          style={{
            backgroundImage: `
              linear-gradient(${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px),
              linear-gradient(90deg, ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        ></div>
        
        {/* Animated Gradient Lines */}
        <div className={`absolute top-1/4 left-0 w-full h-px ${
          darkMode 
            ? 'bg-gradient-to-r from-transparent via-violet-500/30 to-transparent' 
            : 'bg-gradient-to-r from-transparent via-violet-300/30 to-transparent'
        }`}></div>
        <div className={`absolute bottom-1/4 left-0 w-full h-px ${
          darkMode 
            ? 'bg-gradient-to-r from-transparent via-purple-500/30 to-transparent' 
            : 'bg-gradient-to-r from-transparent via-purple-300/30 to-transparent'
        }`}></div>
      </div>
      
      {/* Content with relative z-index */}
      <div className="relative z-10">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        darkMode ? 'bg-black/80 backdrop-blur-xl' : 'bg-white/80 backdrop-blur-xl'
      } border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src={getPublicAssetPath('/le_long.png')} 
                alt="Legitimu" 
                className={`h-8 transition-all ${darkMode ? 'brightness-0 invert filter' : ''}`}
                style={darkMode ? { filter: 'brightness(0) invert(1)' } : {}}
              />
            </div>
            <div className="flex items-center space-x-6">
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full transition-colors ${
                  darkMode 
                    ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-label="Toggle dark mode"
              >
                {darkMode ? (
                  <i className="fa-solid fa-sun text-lg"></i>
                ) : (
                  <i className="fa-solid fa-moon text-lg"></i>
                )}
              </button>
              <Link
                to="/login"
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  darkMode
                    ? 'bg-white text-black hover:bg-gray-100'
                    : 'bg-black text-white hover:bg-gray-900'
                }`}
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className={`text-6xl md:text-8xl font-bold mb-6 tracking-tight ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Agreement Management
            <br />
            <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              Reimagined
            </span>
          </h1>
          <p className={`text-xl md:text-2xl mb-12 max-w-3xl mx-auto ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Streamline your agreement workflow with intelligent automation,
            <br />
            seamless collaboration, and powerful insights.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Link
              to="/support"
              className={`px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 ${
                darkMode
                  ? 'bg-white text-black hover:bg-gray-100'
                  : 'bg-black text-white hover:bg-gray-900'
              }`}
            >
              Get Started
            </Link>
            <button
              className={`px-8 py-4 rounded-full text-lg font-semibold transition-all border-2 ${
                darkMode
                  ? 'border-gray-700 text-white hover:bg-gray-900'
                  : 'border-gray-300 text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className={`text-5xl md:text-6xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Powerful Features
            </h2>
            <p className={`text-xl ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Everything you need to manage agreements efficiently
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className={`p-8 rounded-3xl transition-all hover:scale-105 ${
              darkMode 
                ? 'bg-gray-900 border border-gray-800' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="fa-solid fa-file-contract text-white text-2xl"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Smart Templates
              </h3>
              <p className={`text-lg ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Create and manage agreement templates with dynamic variables. 
                Automatically populate fields and ensure consistency across all documents.
              </p>
            </div>

            {/* Feature 2 */}
            <div className={`p-8 rounded-3xl transition-all hover:scale-105 ${
              darkMode 
                ? 'bg-gray-900 border border-gray-800' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mb-6">
                <i className="fa-solid fa-users text-white text-2xl"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Team Collaboration
              </h3>
              <p className={`text-lg ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Work together seamlessly with mentions, comments, and real-time updates. 
                Keep everyone in the loop throughout the agreement process.
              </p>
            </div>

            {/* Feature 3 */}
            <div className={`p-8 rounded-3xl transition-all hover:scale-105 ${
              darkMode 
                ? 'bg-gray-900 border border-gray-800' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6">
                <i className="fa-solid fa-chart-line text-white text-2xl"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Workflow Tracking
              </h3>
              <p className={`text-lg ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Visualize your agreement pipeline with Kanban boards. 
                Track progress from drafting to approval with real-time status updates.
              </p>
            </div>

            {/* Feature 4 */}
            <div className={`p-8 rounded-3xl transition-all hover:scale-105 ${
              darkMode 
                ? 'bg-gray-900 border border-gray-800' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mb-6">
                <i className="fa-solid fa-shield-halved text-white text-2xl"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Access Control
              </h3>
              <p className={`text-lg ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Granular permissions with role-based access control. 
                Manage who can view, edit, or approve agreements with precision.
              </p>
            </div>

            {/* Feature 5 */}
            <div className={`p-8 rounded-3xl transition-all hover:scale-105 ${
              darkMode 
                ? 'bg-gray-900 border border-gray-800' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-6">
                <i className="fa-solid fa-magnifying-glass text-white text-2xl"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Smart Search
              </h3>
              <p className={`text-lg ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Find agreements instantly with powerful search capabilities. 
                Filter by category, type, status, or any custom criteria.
              </p>
            </div>

            {/* Feature 6 */}
            <div className={`p-8 rounded-3xl transition-all hover:scale-105 ${
              darkMode 
                ? 'bg-gray-900 border border-gray-800' 
                : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="fa-solid fa-clock-rotate-left text-white text-2xl"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Audit Trail
              </h3>
              <p className={`text-lg ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Complete history of all changes and actions. 
                Track who did what, when, and why for full transparency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className={`text-5xl md:text-6xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Simple Pricing
            </h2>
            <p className={`text-xl ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Choose the plan that fits your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div className={`p-8 rounded-3xl border-2 transition-all ${
              darkMode 
                ? 'bg-gray-900 border-gray-800' 
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-2xl font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Starter
              </h3>
              <ul className={`space-y-4 mb-8 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Up to 10 agreements/month
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Basic templates
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Standard support
                </li>
              </ul>
              <Link
                to="/support"
                className={`block w-full text-center py-3 rounded-full font-semibold transition-all ${
                  darkMode
                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Get Started
              </Link>
            </div>

            {/* Professional Plan */}
            <div className={`p-8 rounded-3xl border-2 transition-all transform hover:scale-105 ${
              darkMode 
                ? 'bg-gradient-to-br from-violet-900 to-purple-900 border-violet-700' 
                : 'bg-gradient-to-br from-violet-50 to-purple-50 border-violet-300'
            } relative`}>
              <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  darkMode ? 'bg-violet-700 text-white' : 'bg-violet-600 text-white'
                }`}>
                  Popular
                </span>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Professional
              </h3>
              <ul className={`space-y-4 mb-8 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Unlimited agreements
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Advanced templates
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Priority support
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Custom workflows
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Analytics dashboard
                </li>
              </ul>
              <Link
                to="/support"
                className={`block w-full text-center py-3 rounded-full font-semibold transition-all ${
                  darkMode
                    ? 'bg-white text-black hover:bg-gray-100'
                    : 'bg-violet-600 text-white hover:bg-violet-700'
                }`}
              >
                Get Started
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className={`p-8 rounded-3xl border-2 transition-all ${
              darkMode 
                ? 'bg-gray-900 border-gray-800' 
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-2xl font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Enterprise
              </h3>
              <ul className={`space-y-4 mb-8 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Everything in Professional
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Dedicated support
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  Custom integrations
                </li>
                <li className="flex items-center">
                  <i className="fa-solid fa-check text-violet-600 mr-3"></i>
                  SSO & advanced security
                </li>
              </ul>
              <Link
                to="/login"
                className={`block w-full text-center py-3 rounded-full font-semibold transition-all ${
                  darkMode
                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`p-12 rounded-3xl ${
            darkMode 
              ? 'bg-gradient-to-br from-violet-900 to-purple-900' 
              : 'bg-gradient-to-br from-violet-50 to-purple-50'
          }`}>
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Ready to get started?
            </h2>
            <p className={`text-xl mb-8 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Join thousands of teams already using Legitimu to manage their agreements.
            </p>
            <Link
              to="/support"
              className={`inline-block px-8 py-4 rounded-full text-lg font-semibold transition-all transform hover:scale-105 ${
                darkMode
                  ? 'bg-white text-black hover:bg-gray-100'
                  : 'bg-violet-600 text-white hover:bg-violet-700'
              }`}
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 px-6 border-t ${
        darkMode ? 'border-gray-800' : 'border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <img 
                src={getPublicAssetPath('/le_long.png')} 
                alt="Legitimu" 
                className={`h-6 transition-all ${darkMode ? 'brightness-0 invert filter' : ''}`}
                style={darkMode ? { filter: 'brightness(0) invert(1)' } : {}}
              />
              <span className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                © 2024 Legitimu. All rights reserved.
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="#features"
                className={`text-sm transition-colors ${
                  darkMode 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Features
              </a>
              <a
                href="#pricing"
                className={`text-sm transition-colors ${
                  darkMode 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Pricing
              </a>
              <Link
                to="/login"
                className={`text-sm transition-colors ${
                  darkMode 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
};

export default LandingPage;

